import { buildSettingsNavItems } from "@circle-react/components/CircleAi/Dock/buildSettingsNavItems";
import { isPathActive } from "@circle-react/components/CircleAi/Dock/isPathActive";
import type { BuildNavSubItem } from "@circle-react/components/CircleAi/Dock/types";
import { useSettingsNavLinks } from "@circle-react/components/CircleAi/Dock/useSettingsNavLinks";
import type { SidebarLink } from "@circle-react/components/shared/SettingsNav/CollapsedSidebar/types";
import {
  BACK_TO_COMMUNITY_PATH_KEY,
  useCollapsedSidebarLinksV3,
} from "@circle-react/components/shared/SettingsNav/CollapsedSidebar/useCollapsedSidebarLinksV3";
import type { Link } from "@circle-react/components/shared/SettingsNav/NestedNavBar/types";
import { useLocation } from "@tanstack/react-router";

import { t } from "@/i18n-js/instance";

export interface BreadcrumbCrumb {
  guardNavigation?: boolean;
  href?: string;
  label: string;
}

interface SettingsNavGroup {
  l1Label: string;
  links: Link[];
}

const SETTINGS_HOME_PATH = "/settings";

export const useSettingsBreadcrumb = (): BreadcrumbCrumb[] => {
  const pathname = useLocation({ select: location => location.pathname });
  const settingsHomeLabel = t("circle_ai_dock.settings.landing.title");

  const navLinks = useSettingsNavLinks();
  const sidebarLinks = useCollapsedSidebarLinksV3();

  const groups: SettingsNavGroup[] = [
    { l1Label: t("settings_nav_v3.members.label"), links: navLinks.members },
    { l1Label: t("settings_nav_v3.emails.title"), links: navLinks.email },
    {
      l1Label: t("settings_nav_v3.newsletter.title"),
      links: navLinks.newsletter,
    },
    { l1Label: t("settings_nav_v3.site.label"), links: navLinks.site },
    { l1Label: t("settings_nav_v3.content.label"), links: navLinks.content },
    { l1Label: t("settings_nav_v3.courses.title"), links: navLinks.courses },
    {
      l1Label: t("settings_nav_v3.workflows.label"),
      links: navLinks.workflows,
    },
    { l1Label: t("settings_nav_v3.payments.label"), links: navLinks.paywalls },
    {
      l1Label: t("settings_nav_v3.paywalls_affiliates.label"),
      links: navLinks.affiliates,
    },
    { l1Label: t("settings_nav_v3.settings.label"), links: navLinks.settings },
    { l1Label: t("settings_nav_v3.plans.label"), links: navLinks.plans },
    {
      l1Label: t("settings_nav_v3.branded_apps_dashboard.title"),
      links: navLinks.plus,
    },
    {
      l1Label: t("settings_nav_v3.analytics_v2.label"),
      links: navLinks.analytics_v2,
    },
    {
      l1Label: t("settings_nav_v3.developers.title"),
      links: navLinks.developers,
    },
  ];

  if (pathname === SETTINGS_HOME_PATH) {
    return [{ label: settingsHomeLabel }];
  }

  const homeItems = buildSettingsNavItems(navLinks, {
    includeDisabled: true,
  }).flatMap(group => group.children ?? []);
  const activeHomeItem = findActiveHomeItem(pathname, homeItems);
  if (activeHomeItem) {
    return [
      { label: settingsHomeLabel, href: SETTINGS_HOME_PATH },
      { label: activeHomeItem.label, href: activeHomeItem.to },
    ];
  }

  const active = findActiveGroupLink(pathname, groups);
  if (active) {
    return [{ label: active.link.label }];
  }

  const sidebarLink = findActiveSidebarLink(pathname, sidebarLinks);
  if (sidebarLink) {
    return [{ label: sidebarLink.label }];
  }

  return [{ label: settingsHomeLabel }];
};

interface ActiveGroupLink {
  group: SettingsNavGroup;
  link: Link;
}

const findActiveGroupLink = (
  pathname: string,
  groups: SettingsNavGroup[],
): ActiveGroupLink | undefined => {
  let best: ActiveGroupLink | undefined;
  for (const group of groups) {
    for (const link of group.links) {
      const isMatch =
        isPathActive({ pathname, to: link.path, exact: link.exact }) ||
        pathname.startsWith(`${link.path}/`);
      if (isMatch && (!best || link.path.length > best.link.path.length)) {
        best = { group, link };
      }
    }
  }
  return best;
};

const findActiveHomeItem = (
  pathname: string,
  items: BuildNavSubItem[],
): BuildNavSubItem | undefined => {
  let match: BuildNavSubItem | undefined;
  for (const item of items) {
    const isMatch =
      isPathActive({ pathname, to: item.to, exact: item.exact }) ||
      pathname.startsWith(`${item.to}/`);
    if (isMatch && (!match || item.to.length > match.to.length)) {
      match = item;
    }
  }
  return match;
};

const findActiveSidebarLink = (
  pathname: string,
  links: SidebarLink[],
): SidebarLink | undefined => {
  let match: SidebarLink | undefined;
  for (const link of links) {
    if (!link.enabled || link.pathKey === BACK_TO_COMMUNITY_PATH_KEY) {
      continue;
    }
    const isMatch =
      isPathActive({ pathname, to: link.path, exact: true }) ||
      pathname.startsWith(`${link.path}/`);
    if (isMatch && (!match || link.path.length > match.path.length)) {
      match = link;
    }
  }
  return match;
};
